import {
  createServerDatabaseRootDocId,
  createServerDatabaseRowShardDocId,
  DATABASE_LARGE_DATA_POLICY,
  isHiddenSystemManagedDatabase,
  isLocalDatabaseRootDocId,
  isServerDatabaseRootDocId,
  normalizeDatabaseBlockId,
  normalizeDatabaseShardManifest,
} from '@streamlain/database-identity';
export type DatabaseSubdocBlockIdentity = {
  id?: unknown;
  databaseId?: unknown;
  databaseRootDocId?: unknown;
  databaseShardManifest?: unknown;
  flavour?: unknown;
  managedBy?: unknown;
  visible?: unknown;
};

export type DatabaseSubdocDataSourceRef = {
  databaseId?: unknown;
  blockId?: unknown;
  databaseRootDocId?: unknown;
  databaseShardManifest?: unknown;
};

export const DATABASE_BLOCK_FLAVOUR = 'affine:database';

export const DEFAULT_DATABASE_ROW_SHARD_ID = '00001';

export const INITIAL_DATABASE_ROW_WINDOW_LIMIT =
  DATABASE_LARGE_DATA_POLICY.ui.defaultPageSize;

export const LOCAL_FIRST_DATABASE_CACHE_ROW_LIMIT =
  DATABASE_LARGE_DATA_POLICY.ui.visibleRowWindowLimit;

export function addDocId(target: Set<string>, docId: unknown): void {
  if (typeof docId === 'string' && docId.startsWith('db$')) {
    target.add(docId);
  }
}

export function isDatabaseRootDocId(
  docId: string,
  workspaceId: string
): boolean {
  return (
    isServerDatabaseRootDocId(docId, workspaceId) ||
    isLocalDatabaseRootDocId(docId)
  );
}

export function addRootDocId(
  target: Set<string>,
  docId: unknown,
  workspaceId: string
): void {
  if (typeof docId === 'string' && isDatabaseRootDocId(docId, workspaceId)) {
    target.add(docId);
  }
}

export function toDatabaseDocId(docId: unknown): string | null {
  return typeof docId === 'string' && docId.startsWith('db$') ? docId : null;
}

export function addDefaultServerDocIds(
  target: Set<string>,
  workspaceId: string,
  databaseId: unknown
): void {
  if (typeof databaseId !== 'string' || !databaseId) {
    return;
  }

  try {
    target.add(createServerDatabaseRootDocId({ databaseId, workspaceId }));
    target.add(
      createServerDatabaseRowShardDocId({
        databaseId,
        shardId: DEFAULT_DATABASE_ROW_SHARD_ID,
        workspaceId,
      })
    );
  } catch {
    // Invalid database/workspace identifiers cannot produce canonical doc ids.
  }
}

export function addDefaultServerRootDocId(
  target: Set<string>,
  workspaceId: string,
  databaseId: unknown
): void {
  if (typeof databaseId !== 'string' || !databaseId) {
    return;
  }

  try {
    target.add(createServerDatabaseRootDocId({ databaseId, workspaceId }));
  } catch {
    // Invalid database/workspace identifiers cannot produce canonical doc ids.
  }
}

export function addInitialManifestRowShardDocIds(
  target: Set<string>,
  manifest: ReturnType<typeof normalizeDatabaseShardManifest>
): void {
  if (!manifest) {
    return;
  }

  addDocId(target, manifest.databaseRootDocId);

  const totalRows = manifest.shards.reduce(
    (sum, shard) => sum + shard.rowCount,
    0
  );
  const rowLimit =
    totalRows <= INITIAL_DATABASE_ROW_WINDOW_LIMIT
      ? totalRows
      : INITIAL_DATABASE_ROW_WINDOW_LIMIT;
  if (rowLimit <= 0) {
    return;
  }

  let shardStart = 0;
  for (const shard of manifest.shards) {
    const shardEnd = shardStart + shard.rowCount;
    if (shardEnd > 0 && shardStart < rowLimit) {
      addDocId(target, shard.serverDocId);
    }
    if (shardStart >= rowLimit) {
      break;
    }
    shardStart = shardEnd;
  }
}

export function addLocalFirstManifestRowShardDocIds(
  target: Set<string>,
  manifest: ReturnType<typeof normalizeDatabaseShardManifest>
): void {
  if (!manifest) {
    return;
  }

  addDocId(target, manifest.databaseRootDocId);

  let cachedRows = 0;
  for (const shard of manifest.shards) {
    if (cachedRows >= LOCAL_FIRST_DATABASE_CACHE_ROW_LIMIT) {
      break;
    }
    addDocId(target, shard.serverDocId);
    cachedRows += shard.rowCount;
  }
}

export function normalizeDatabaseIdHint(databaseId: unknown): unknown {
  return typeof databaseId === 'string'
    ? normalizeDatabaseBlockId(databaseId)
    : databaseId;
}

export function dataSourceRefToDatabaseBlockIdentity(
  ref: DatabaseSubdocDataSourceRef
): DatabaseSubdocBlockIdentity {
  return {
    databaseId: normalizeDatabaseIdHint(ref.databaseId ?? ref.blockId),
    databaseRootDocId: ref.databaseRootDocId,
    databaseShardManifest: ref.databaseShardManifest,
    flavour: DATABASE_BLOCK_FLAVOUR,
  };
}

export function isDiscoverableDatabaseSubdocIdentity(
  block: DatabaseSubdocBlockIdentity
): boolean {
  if (block.flavour !== DATABASE_BLOCK_FLAVOUR) {
    return false;
  }

  const databaseId =
    typeof block.databaseId === 'string' ? block.databaseId : block.id;
  return !isHiddenSystemManagedDatabase({
    id: typeof databaseId === 'string' ? databaseId : undefined,
    managedBy: block.managedBy,
    visible: block.visible,
  });
}

export function collectDatabaseSubdocIdsFromDatabaseBlocks(
  blocks: Iterable<DatabaseSubdocBlockIdentity>,
  workspaceId: string
): string[] {
  const subdocIds = new Set<string>();

  for (const block of blocks) {
    if (!isDiscoverableDatabaseSubdocIdentity(block)) {
      continue;
    }

    addDocId(subdocIds, block.databaseRootDocId);
    addDefaultServerDocIds(subdocIds, workspaceId, block.databaseId);

    try {
      const manifest = normalizeDatabaseShardManifest(
        block.databaseShardManifest
      );
      if (!manifest) {
        continue;
      }

      addDocId(subdocIds, manifest.databaseRootDocId);
      for (const shard of manifest.shards) {
        addDocId(subdocIds, shard.serverDocId);
      }
    } catch {
      // Corrupt manifest data must not block deterministic root/default preload.
    }
  }

  return Array.from(subdocIds);
}

export function collectInitialDatabaseSubdocIdsFromDatabaseBlocks(
  blocks: Iterable<DatabaseSubdocBlockIdentity>,
  workspaceId: string
): string[] {
  const subdocIds = new Set<string>();

  for (const block of blocks) {
    if (!isDiscoverableDatabaseSubdocIdentity(block)) {
      continue;
    }

    addDocId(subdocIds, block.databaseRootDocId);
    addDefaultServerDocIds(subdocIds, workspaceId, block.databaseId);

    try {
      addInitialManifestRowShardDocIds(
        subdocIds,
        normalizeDatabaseShardManifest(block.databaseShardManifest)
      );
    } catch {
      // Corrupt manifest data must not block deterministic root/default preload.
    }
  }

  return Array.from(subdocIds);
}

export function collectLocalFirstDatabaseSubdocIdsFromDatabaseBlocks(
  blocks: Iterable<DatabaseSubdocBlockIdentity>,
  workspaceId: string
): string[] {
  const subdocIds = new Set<string>();

  for (const block of blocks) {
    if (!isDiscoverableDatabaseSubdocIdentity(block)) {
      continue;
    }

    addDocId(subdocIds, block.databaseRootDocId);
    addDefaultServerDocIds(subdocIds, workspaceId, block.databaseId);

    try {
      addLocalFirstManifestRowShardDocIds(
        subdocIds,
        normalizeDatabaseShardManifest(block.databaseShardManifest)
      );
    } catch {
      // Corrupt manifest data must not block deterministic root/default preload.
    }
  }

  return Array.from(subdocIds);
}

export function collectDatabaseRootSubdocIdsFromDatabaseBlocks(
  blocks: Iterable<DatabaseSubdocBlockIdentity>,
  workspaceId: string
): string[] {
  const rootDocIds = new Set<string>();

  for (const block of blocks) {
    if (!isDiscoverableDatabaseSubdocIdentity(block)) {
      continue;
    }

    addRootDocId(rootDocIds, block.databaseRootDocId, workspaceId);
    addDefaultServerRootDocId(rootDocIds, workspaceId, block.databaseId);

    try {
      const manifest = normalizeDatabaseShardManifest(
        block.databaseShardManifest
      );
      if (!manifest) {
        continue;
      }
      addRootDocId(rootDocIds, manifest.databaseRootDocId, workspaceId);
    } catch {
      // Corrupt manifest data must not block deterministic root preload.
    }
  }

  return Array.from(rootDocIds);
}

export function collectDatabaseSubdocIdsFromDataSourceRefs(
  refs: Iterable<DatabaseSubdocDataSourceRef>,
  workspaceId: string
): string[] {
  return collectDatabaseSubdocIdsFromDatabaseBlocks(
    Array.from(refs, dataSourceRefToDatabaseBlockIdentity),
    workspaceId
  );
}
